import { useMemo } from 'react';

import { LOW_BATTERY_THRESHOLD } from '@/utils/constants/lowBatteryThreshold';

import { useSensorsContext } from '@/contexts/SensorsContext';

/**
 * Custom React hook to retrieve a list of devices (sensors and hub) with critically low battery levels.
 *
 * @returns {Array.<{id: string, title: string, isHub: boolean}>} An array of device objects containing:
 * - `id`: Unique identifier for the device (sensor ID or "hub").
 * - `title`: Human-readable title of the device, retrieved from database; defaults to "Sensor" if not found.
 * - `isHub`: Boolean indicating whether the device is the Hub.
 *
 * @example const lowBatteryDevices = useLowBatteryDevices();
 *
 * @remarks
 * - A battery level of 20% or below is considered critically low, below this we won't allow the user to
 * start a measurement. Since the device can power down during the measurement if the battery is too low.
 *
 * @edgecases
 * - Sensors without corresponding entries in `sensorsInDatabase` will default to a generic title "Sensor"
 *
 * @dependencies Relies on `useSensorsContext` to fetch current sensor states and hub battery level information.
 */
export const useLowBatteryDevices = () => {
  const { state, hubBatteryLevel } = useSensorsContext();

  const { batteryLevel, sensorsInDatabase } = state;

  return useMemo(() => {
    const sensors = Array.from(batteryLevel.entries());

    return [
      ...sensors
        .filter(([, level]) => level <= LOW_BATTERY_THRESHOLD)
        .map(([id]) => ({
          id,
          title: sensorsInDatabase.find((sensor) => sensor.id === id)?.title ?? 'Sensor',
          isHub: false,
        })),
      ...(hubBatteryLevel !== undefined && hubBatteryLevel <= LOW_BATTERY_THRESHOLD
        ? [{ id: 'hub', title: 'Hub', isHub: true }]
        : []),
    ];
  }, [batteryLevel, sensorsInDatabase, hubBatteryLevel]);
};
